// client/src/components/admin/CreateLeague.js
import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ApiLeagues from '../../api/ApiLeagues'
import ApiRegions from '../../api/ApiRegions'
import PageLayout from '../common/layout/PageLayout'
import Modal from '../ui/Modal/Modal'
import './CreateLeague.css'
import toastr from 'toastr'
import logger from '../../utils/logger'
import useAuth from '../../hooks/useAuth'
import DatesRegistrationSection from './league-form/DatesRegistrationSection'
import { isAdmin as isAdminRole } from '../../utils/userRoles'
import useLeagueFormState from './league-form/hooks/useLeagueFormState'
import useLeagueEditRestrictions from './league-form/hooks/useLeagueEditRestrictions'
import {
  INITIAL_FORM_DATA,
  OWNER_CONFIRMATION_FIELDS,
  RESTRICTED_FIELDS_FOR_ADMINS,
} from './league-form/leagueFormConstants'
import {
  toCreatePayload,
  toUpdatePayload,
  validateLeagueForm,
  formatDate,
} from './league-form/leagueFormUtils'
import BasicInformationSection from './league-form/sections/BasicInformationSection'
import LeagueSettingsSection from './league-form/sections/LeagueSettingsSection'
import LeagueLevelsSection from './league-form/sections/LeagueLevelsSection'
import AdditionalInformationSection from './league-form/sections/AdditionalInformationSection'
import LeagueExtensionSection from './league-form/sections/LeagueExtensionSection'
import LeagueFormActions from './league-form/sections/LeagueFormActions'

export default function CreateLeague() {
  const navigate = useNavigate()
  const { leagueId } = useParams()
  const { user } = useAuth()
  const isEditMode = !!leagueId
  const [regions, setRegions] = useState([])
  const [showAddRegion, setShowAddRegion] = useState(false)
  const [showNavigationModal, setShowNavigationModal] = useState(false)
  const [showRestrictedEditModal, setShowRestrictedEditModal] = useState(false)
  const [pendingSubmitData, setPendingSubmitData] = useState(null)
  const [loading, setLoading] = useState(isEditMode)
  const [originalLeagueData, setOriginalLeagueData] = useState(null)
  const [newRegion, setNewRegion] = useState({
    city_area: '',
    state: '',
  })
  const [leagueLevels, setLeagueLevels] = useState([])
  const [levelToAdd, setLevelToAdd] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [extensionDays, setExtensionDays] = useState(7)
  const [isExtendingLeague, setIsExtendingLeague] = useState(false)

  const {
    formData,
    setFormData,
    errors,
    setErrors,
    handleInputChange,
    handleDateSectionChange,
  } = useLeagueFormState()

  const { isLeagueStarted, isFieldDisabled, hasRestrictedChanges } =
    useLeagueEditRestrictions({
      isEditMode,
      leagueStatus: formData.league_status,
      user,
      originalLeagueData,
    })

  const refreshLeagueLevels = useCallback(async () => {
    if (!leagueId) return
    try {
      const levelResponse = await ApiLeagues.getLeagueLevels(leagueId)
      setLeagueLevels(levelResponse.league_levels || [])
    } catch (error) {
      logger.error('Failed to refresh league levels:', error)
      toastr.error('Failed to load league levels')
    }
  }, [leagueId])

  useEffect(() => {
    const loadRegions = async () => {
      try {
        const regionData = await ApiRegions.getAll()
        setRegions(regionData)
      } catch (error) {
        console.error('Failed to load regions:', error)
      }
    }

    const loadLeague = async () => {
      try {
        const leagueData = await ApiLeagues.getLeague(leagueId)

        const formattedLeague = {
          ...leagueData,
          match_format:
            leagueData.match_format || INITIAL_FORM_DATA.match_format,
          playoff_min_matches_required:
            leagueData.playoff_min_matches_required || 3,
          registration_start: formatDate(leagueData.registration_start),
          registration_end: formatDate(leagueData.registration_end),
          play_start: formatDate(leagueData.play_start),
          play_end: formatDate(leagueData.play_end),
        }

        setFormData(formattedLeague)
        setOriginalLeagueData(formattedLeague)
        await refreshLeagueLevels()
      } catch (error) {
        logger.error('Failed to load league:', error, { leagueId })
        toastr.error('Failed to load league details')
      } finally {
        setLoading(false)
      }
    }

    loadRegions()
    if (isEditMode) {
      loadLeague()
    }
  }, [leagueId, isEditMode, refreshLeagueLevels, setFormData])

  const handleAddRegion = async (e) => {
    e.preventDefault()
    if (!newRegion.city_area || !newRegion.state) {
      setErrors((prev) => ({
        ...prev,
        newRegion: 'Both City/Area and State are required',
      }))
      return
    }

    try {
      const result = await ApiRegions.create(newRegion)
      const createdRegion = { ...newRegion, id: result.id }
      setRegions((prev) => [...prev, createdRegion])
      setFormData((prev) => ({ ...prev, region_id: result.id }))
      setNewRegion({ city_area: '', state: '' })
      setShowAddRegion(false)
      toastr.success('Region added successfully!')
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        newRegion: 'Failed to create region: ' + error.message,
      }))
    }
  }

  const handleNewRegionChange = (e) => {
    const { name, value } = e.target
    setNewRegion((prev) => ({
      ...prev,
      [name]: value,
    }))
    if (errors.newRegion) {
      setErrors((prev) => ({ ...prev, newRegion: null }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const validationErrors = validateLeagueForm({ formData, isEditMode })
    setErrors(validationErrors)

    if (Object.keys(validationErrors).length > 0 || isSubmitting) return

    let formattedData = isEditMode
      ? toUpdatePayload(formData)
      : toCreatePayload(formData)

    if (isEditMode && isLeagueStarted && user?.user_type === 'admin') {
      const adminEditableData = Object.fromEntries(
        Object.entries(formattedData).filter(
          ([key]) => !RESTRICTED_FIELDS_FOR_ADMINS.includes(key),
        ),
      )

      formattedData = Object.fromEntries(
        Object.entries(adminEditableData).filter(([key, value]) => {
          if (!originalLeagueData) return true
          return originalLeagueData[key] !== value
        }),
      )

      const keysToUpdate = Object.keys(formattedData)
      if (keysToUpdate.length === 0) {
        setErrors((prev) => ({
          ...prev,
          submit:
            'No editable fields for admins once a league has started. Ask an owner to make structural changes.',
        }))
        return
      }
    }

    if (isEditMode && hasRestrictedChanges(formattedData)) {
      setPendingSubmitData(formattedData)
      setShowRestrictedEditModal(true)
      return
    }

    await submitLeague(formattedData)
  }

  const submitLeague = async (formattedData) => {
    setIsSubmitting(true)
    try {
      if (isEditMode) {
        await ApiLeagues.updateLeague(leagueId, formattedData)
        toastr.success('League updated successfully!')
      } else {
        await ApiLeagues.create(formattedData)
        toastr.success('League created successfully!')
      }
      setShowNavigationModal(true)
    } catch (error) {
      console.error(
        `Failed to ${isEditMode ? 'update' : 'create'} league:`,
        error,
      )

      if (error.response?.data?.forbidden_fields) {
        const { forbidden_fields, league_status, user_type } =
          error.response.data
        setErrors((prev) => ({
          ...prev,
          submit: `Cannot edit ${forbidden_fields.join(
            ', ',
          )} in ${league_status} status as ${user_type}. Contact an owner for help.`,
        }))
      } else {
        setErrors((prev) => ({
          ...prev,
          submit: error.message || 'An error occurred while saving the league',
        }))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSkillLevelSelection = (levelValue, checked) => {
    setFormData((prev) => {
      const currentLevels = prev.allowed_skill_levels || []
      const nextLevels = checked
        ? [...new Set([...currentLevels, levelValue])]
        : currentLevels.filter((value) => value !== levelValue)
      return {
        ...prev,
        allowed_skill_levels: nextLevels.sort((a, b) => a - b),
      }
    })
    setErrors((prev) => ({ ...prev, allowed_skill_levels: null }))
  }

  const handleAddLevel = async () => {
    if (!levelToAdd) return
    try {
      await ApiLeagues.addLeagueLevels(leagueId, [parseFloat(levelToAdd)])
      setLevelToAdd('')
      await refreshLeagueLevels()
    } catch {
      toastr.error('Failed to add league level')
    }
  }

  const handleDeactivateLevel = async (skillLevel) => {
    try {
      await ApiLeagues.deactivateLeagueLevels(leagueId, [skillLevel])
      await refreshLeagueLevels()
    } catch {
      toastr.error('Failed to deactivate league level')
    }
  }

  const handleExtendLeague = async () => {
    const parsedDays = parseInt(extensionDays, 10)

    if (!Number.isInteger(parsedDays) || parsedDays <= 0) {
      toastr.error('Please enter a valid extension in days')
      return
    }

    if (!leagueId || isExtendingLeague) return

    setIsExtendingLeague(true)
    try {
      const result = await ApiLeagues.extendLeague(leagueId, parsedDays)
      const updatedPlayEnd = formatDate(result.league.play_end)

      setFormData((prev) => ({
        ...prev,
        play_end: updatedPlayEnd,
      }))
      setOriginalLeagueData((prev) =>
        prev
          ? {
              ...prev,
              play_end: updatedPlayEnd,
            }
          : prev,
      )

      toastr.success(
        `League extended by ${parsedDays} day${parsedDays === 1 ? '' : 's'}. Email sent to ${result.emails_sent} participant${result.emails_sent === 1 ? '' : 's'}.`,
      )

      if (result.emails_failed > 0) {
        toastr.warning(
          `${result.emails_failed} extension email${result.emails_failed === 1 ? '' : 's'} failed to send`,
        )
      }
    } catch (error) {
      toastr.error(error.message || 'Failed to extend league')
    } finally {
      setIsExtendingLeague(false)
    }
  }

  const handleConfirmedSubmit = () => {
    setShowRestrictedEditModal(false)
    if (pendingSubmitData) {
      submitLeague(pendingSubmitData)
      setPendingSubmitData(null)
    }
  }

  const handleCancelSubmit = () => {
    setShowRestrictedEditModal(false)
    setPendingSubmitData(null)
  }

  if (loading)
    return (
      <PageLayout title="Loading...">
        <p>Loading...</p>
      </PageLayout>
    )

  return (
    <PageLayout title={isEditMode ? 'Edit League' : 'Create New League'}>
      <div className="create-league-container">
        <div className="content-wrapper">
          <form onSubmit={handleSubmit} className="create-league-form">
            <BasicInformationSection
              formData={formData}
              errors={errors}
              handleInputChange={handleInputChange}
              isFieldDisabled={isFieldDisabled}
            />

            <DatesRegistrationSection
              formData={formData}
              errors={errors}
              isFieldDisabled={isFieldDisabled}
              leagueStatus={formData.league_status}
              onChange={handleDateSectionChange}
            />

            <LeagueSettingsSection
              formData={formData}
              errors={errors}
              isEditMode={isEditMode}
              regions={regions}
              showAddRegion={showAddRegion}
              setShowAddRegion={setShowAddRegion}
              newRegion={newRegion}
              handleInputChange={handleInputChange}
              handleNewRegionChange={handleNewRegionChange}
              handleAddRegion={handleAddRegion}
              isFieldDisabled={isFieldDisabled}
              handleSkillLevelSelection={handleSkillLevelSelection}
            />

            {isEditMode && (
              <LeagueLevelsSection
                leagueLevels={leagueLevels}
                levelToAdd={levelToAdd}
                setLevelToAdd={setLevelToAdd}
                handleAddLevel={handleAddLevel}
                handleDeactivateLevel={handleDeactivateLevel}
              />
            )}

            <AdditionalInformationSection
              formData={formData}
              handleInputChange={handleInputChange}
              isFieldDisabled={isFieldDisabled}
            />

            {isEditMode && isAdminRole(user) && (
              <LeagueExtensionSection
                extensionDays={extensionDays}
                setExtensionDays={setExtensionDays}
                handleExtendLeague={handleExtendLeague}
                isExtendingLeague={isExtendingLeague}
              />
            )}

            {errors.submit && (
              <div className="error-message submit-error">{errors.submit}</div>
            )}

            <LeagueFormActions
              navigate={navigate}
              isSubmitting={isSubmitting}
              isEditMode={isEditMode}
            />
          </form>
        </div>

        <Modal
          isOpen={showRestrictedEditModal}
          onClose={handleCancelSubmit}
          title="⚠️ Edit Started League"
          hideDefaultButtons={true}
        >
          <div className="alert alert-warning">
            <h6>
              <i className="fas fa-exclamation-triangle me-2"></i>
              League Modification Warning
            </h6>
            <p className="mb-0">
              This league has already started (
              {formData.league_status?.replace('_', ' ')?.toUpperCase()}).
              Modifying core league settings may affect existing players and
              matches.
            </p>
          </div>

          <div className="my-3">
            <h6>You are about to modify:</h6>
            <ul className="mb-0">
              {pendingSubmitData &&
                originalLeagueData &&
                Object.keys(pendingSubmitData)
                  .filter(
                    (key) =>
                      OWNER_CONFIRMATION_FIELDS.includes(key) &&
                      (() => {
                        const dateFields = [
                          'registration_start',
                          'registration_end',
                          'play_start',
                          'play_end',
                        ]
                        if (dateFields.includes(key)) {
                          const pendingValue = pendingSubmitData[key]
                          const originalValue = originalLeagueData[key]
                            ? new Date(originalLeagueData[key]).toISOString()
                            : null
                          return pendingValue !== originalValue
                        }
                        return (
                          pendingSubmitData[key] !== originalLeagueData[key]
                        )
                      })(),
                  )
                  .map((field) => (
                    <li key={field}>
                      <strong>
                        {field
                          .replace(/_/g, ' ')
                          .replace(/\b\w/g, (l) => l.toUpperCase())}
                      </strong>
                    </li>
                  ))}
            </ul>
          </div>

          <div className="alert alert-info">
            <h6>
              <i className="fas fa-info-circle me-2"></i>
              Owner Privileges
            </h6>
            <p className="mb-0">
              As a league owner, you can make these changes, but please ensure
              they won't negatively impact existing players or disrupt the
              league progress.
            </p>
          </div>

          <div className="d-flex gap-2 mt-4">
            <button
              type="button"
              className="btn btn-secondary flex-fill"
              onClick={handleCancelSubmit}
            >
              <i className="fas fa-times me-2"></i>
              Cancel Changes
            </button>
            <button
              type="button"
              className="btn btn-primary flex-fill"
              onClick={handleConfirmedSubmit}
              disabled={isSubmitting}
            >
              <i className="fas fa-exclamation-triangle me-2"></i>
              {isSubmitting ? 'Saving...' : 'Confirm Changes'}
            </button>
          </div>
        </Modal>

        <Modal
          isOpen={showNavigationModal}
          onClose={() => setShowNavigationModal(false)}
          title="League Saved"
          hideDefaultButtons={true}
        >
          <p>
            Your league has been saved successfully. What would you like to do?
          </p>
          <div className="mt-4">
            <button
              type="button"
              className="btn btn-secondary me-2"
              onClick={() => setShowNavigationModal(false)}
            >
              Stay Here
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => navigate('/admin/leagues-registrations')}
              data-track="admin.go_to_league_management"
            >
              Go to League Management
            </button>
          </div>
        </Modal>
      </div>
    </PageLayout>
  )
}
